/**
 * Cookieless secondary page analytics — consent-independent beacons.
 *
 * Uses fetch + keepalive + credentials:omit (NOT navigator.sendBeacon primary).
 * No consentManager checks. No localStorage/sessionStorage visitor IDs.
 */

import {
  isPublicAnalyticsPath,
  normalizePathForBeacon,
} from './cookieless-analytics-paths.js';

const TRACK_URL = '/api/public/cookieless-analytics/page-views/';
const ENGAGEMENT_URL = '/api/public/cookieless-analytics/page-views/engagement/';
const OUTBOUND_CLICK_URL = '/api/public/cookieless-analytics/outbound-clicks/';

const MAX_PRODUCT_ID_LEN = 100;
const MAX_MARKETPLACE_LEN = 30;
const MAX_DESTINATION_LEN = 2000;

// Accumulated *visible* milliseconds, not wall clock. This used to be a single
// start timestamp read once, which meant time spent on a backgrounded tab was
// counted as reading and a visitor who tabbed away for two seconds and came back
// for five minutes was recorded at two seconds.
let _visibleMs = 0;
let _visibleSinceMs = 0;
let _lastSentMs = 0;

/**
 * Current path for beacon body: pathname+search, then search/TF strip + length cap.
 * @returns {string}
 */
function currentBeaconPath() {
  const raw = `${window.location.pathname || '/'}${window.location.search || ''}`;
  return normalizePathForBeacon(raw);
}

/**
 * @param {string} url
 * @param {object} payload
 */
function postJson(url, payload) {
  try {
    const body = JSON.stringify(payload);
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'omit',
    }).catch(() => {});
  } catch {
    // Swallow — analytics must never break the page
  }
}

/**
 * Fire a cookieless page-view beacon if the current path is allowlisted.
 */
export function postPageView() {
  const path = currentBeaconPath();
  if (!isPublicAnalyticsPath(path)) {
    return;
  }

  const payload = {
    path,
    domain: window.location.hostname || '',
    referrer: document.referrer || null,
  };

  try {
    if (typeof window.innerWidth === 'number' && window.innerWidth > 0) {
      payload.viewport_width = window.innerWidth;
    }
  } catch {
    /* ignore */
  }

  try {
    if (typeof navigator !== 'undefined' && navigator.language) {
      payload.language = String(navigator.language).slice(0, 32);
    }
  } catch {
    /* ignore */
  }

  postJson(TRACK_URL, payload);
}

/**
 * Fire a cookieless outbound-click beacon.
 *
 * Every other click path — trackProductClick, AnalyticsManager.trackClick, the
 * GA4 event — returns early without analytics consent, so the admin console only
 * ever counted the small share of visitors who accepted the cookie and its click
 * totals read far below what the affiliate network reported. This beacon runs on
 * the same terms as the page view above: no consent cookie, no stored visitor
 * id, and the destination URL is only sent so the server can keep its host.
 *
 * @param {object} [options]
 * @param {string} [options.productId]
 * @param {string} [options.marketplace]
 * @param {string} [options.destinationUrl]
 */
export function postOutboundClick(options = {}) {
  if (typeof window === 'undefined') {
    return;
  }
  const path = currentBeaconPath();
  if (!isPublicAnalyticsPath(path)) {
    return;
  }

  const payload = {
    path,
    domain: window.location.hostname || '',
  };
  if (options.productId) {
    payload.product_id = String(options.productId).slice(0, MAX_PRODUCT_ID_LEN);
  }
  if (options.marketplace) {
    payload.marketplace = String(options.marketplace).slice(0, MAX_MARKETPLACE_LEN);
  }
  if (options.destinationUrl) {
    payload.destination_url = String(options.destinationUrl).slice(0, MAX_DESTINATION_LEN);
  }

  // keepalive matters here more than anywhere else: the click is about to open a
  // new tab or navigate away.
  postJson(OUTBOUND_CLICK_URL, payload);
}

/** Close the open visible run, if any, and fold it into the total. */
function pauseVisibleClock() {
  if (_visibleSinceMs) {
    _visibleMs += Math.max(0, Date.now() - _visibleSinceMs);
    _visibleSinceMs = 0;
  }
}

/** Start a visible run if one is not already open. */
function resumeVisibleClock() {
  if (!_visibleSinceMs) {
    _visibleSinceMs = Date.now();
  }
}

/**
 * Fire an engagement update carrying the running visible-time total.
 *
 * Sent every time the page is hidden rather than once per load, so a visitor who
 * keeps reading after a first hide is measured properly. The server keeps the
 * larger value, so re-sending a growing total is safe and out-of-order delivery
 * cannot shrink it.
 */
export function postEngagement() {
  const path = currentBeaconPath();
  if (!isPublicAnalyticsPath(path)) {
    return;
  }

  pauseVisibleClock();
  const elapsed = Math.round(_visibleMs);
  if (elapsed <= 0 || elapsed <= _lastSentMs) {
    return;
  }

  _lastSentMs = elapsed;
  const payload = {
    path,
    domain: window.location.hostname || '',
    time_on_page_ms: elapsed,
  };
  try {
    if (typeof window.innerWidth === 'number' && window.innerWidth > 0) {
      payload.viewport_width = window.innerWidth;
    }
  } catch {
    /* ignore */
  }
  postJson(ENGAGEMENT_URL, payload);
}

function onVisibilityChange() {
  if (typeof document === 'undefined') {
    return;
  }
  if (document.visibilityState === 'hidden') {
    postEngagement();
  } else {
    resumeVisibleClock();
  }
}

function onPageHide() {
  postEngagement();
}

/**
 * Restore from the back/forward cache.
 *
 * A bfcache restore is a fresh visit to the reader but not a fresh document, so
 * the module-level init guard below stayed set, no new page view was recorded,
 * and engagement could never fire again. Back-button traffic was invisible.
 */
function onPageShow(event) {
  if (!event || !event.persisted) {
    return;
  }
  startPageMeasurement();
  postPageView();
}

/** Reset the visible-time counters for a new (or restored) page view. */
function startPageMeasurement() {
  _visibleMs = 0;
  _visibleSinceMs = 0;
  _lastSentMs = 0;
  if (typeof document === 'undefined' || document.visibilityState !== 'hidden') {
    resumeVisibleClock();
  }
}

/**
 * Idempotent public-page init: page-view beacon + engagement listeners.
 */
export function initializeCookielessPageAnalytics() {
  if (typeof window === 'undefined') {
    return;
  }
  if (window.__UF_COOKIELESS_ANALYTICS_INIT__) {
    return;
  }
  window.__UF_COOKIELESS_ANALYTICS_INIT__ = true;

  startPageMeasurement();
  postPageView();

  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', onPageHide);
  window.addEventListener('pageshow', onPageShow);
}

if (typeof window !== 'undefined') {
  window.initializeCookielessPageAnalytics = initializeCookielessPageAnalytics;
  window.postCookielessOutboundClick = postOutboundClick;
}
